/*
  Interactions des sections bien-être (bw-header, bw-home, bw-footer).
  Le contenu de chaque section vit dans un shadow DOM déclaratif (<bw-layout>),
  ce script s'exécute donc sur chaque shadowRoot.
*/
(() => {
  if (window.__bwInit) return;
  window.__bwInit = true;

  // Navigateurs sans shadow DOM déclaratif, et sections rechargées dans l'éditeur de thème.
  const upgradeShadowRoots = (root) => {
    root.querySelectorAll('template[shadowrootmode]').forEach((t) => {
      const host = t.parentNode;
      if (!host || host.shadowRoot) return;
      try {
        host.attachShadow({ mode: t.getAttribute('shadowrootmode') }).append(t.content);
        t.remove();
      } catch (e) {}
    });
  };

  const setOpen = (el, open) => el.setAttribute('data-state', open ? 'open' : 'closed');
  const isOpen = (el) => el.getAttribute('data-state') === 'open';

  // En-tête fixe : la barre de navigation reste en haut une fois dépassée.
  const initSticky = (scope) => {
    scope.querySelectorAll('[data-header-sticky]').forEach((bar) => {
      if (bar.__bw) return;
      bar.__bw = true;
      const sentinel = document.createElement('div');
      const spacer = document.createElement('div');
      bar.before(sentinel);
      let stuck = false;
      const update = () => {
        const shouldStick = sentinel.getBoundingClientRect().top < 0;
        if (shouldStick === stuck) return;
        stuck = shouldStick;
        if (stuck) {
          spacer.style.height = `${bar.getBoundingClientRect().height}px`;
          bar.before(spacer);
          Object.assign(bar.style, { position: 'fixed', top: '0', left: '0', right: '0', zIndex: '40' });
          bar.setAttribute('data-stuck', '');
        } else {
          spacer.remove();
          Object.assign(bar.style, { position: '', top: '', left: '', right: '', zIndex: '' });
          bar.removeAttribute('data-stuck');
        }
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
    });
  };

  // Méga-menu : ouverture au survol et au clavier.
  const initMenus = (scope) => {
    scope.querySelectorAll('[data-menu]').forEach((menu) => {
      if (menu.__bw) return;
      menu.__bw = true;
      const trigger = menu.querySelector('[data-menu-trigger]');
      let closeTimer;
      const open = () => {
        clearTimeout(closeTimer);
        setOpen(menu, true);
        trigger?.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        setOpen(menu, false);
        trigger?.setAttribute('aria-expanded', 'false');
      };
      menu.addEventListener('pointerenter', (e) => e.pointerType !== 'touch' && open());
      menu.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'touch') closeTimer = setTimeout(close, 150);
      });
      menu.addEventListener('focusin', open);
      menu.addEventListener('focusout', (e) => {
        if (!menu.contains(e.relatedTarget)) close();
      });
      menu.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          close();
          trigger?.focus();
        }
      });
      // Sur écran tactile, le premier appui ouvre le menu au lieu de suivre le lien.
      trigger?.addEventListener('click', (e) => {
        if (window.matchMedia('(hover: none)').matches && !isOpen(menu)) {
          e.preventDefault();
          open();
        }
      });
    });
  };

  // Menu mobile (panneau latéral).
  const initOverlays = (scope) => {
    scope.querySelectorAll('[data-overlay]').forEach((overlay) => {
      if (overlay.__bw) return;
      overlay.__bw = true;
      const name = overlay.getAttribute('data-overlay');
      const openers = scope.querySelectorAll(`[data-overlay-open="${name}"]`);
      let lastFocus = null;
      const toggle = (open) => {
        setOpen(overlay, open);
        openers.forEach((b) => b.setAttribute('aria-expanded', String(open)));
        document.documentElement.style.overflow = open ? 'hidden' : '';
        if (open) {
          lastFocus = scope.activeElement || document.activeElement;
          overlay.querySelector('a[href], button, input')?.focus();
        } else {
          lastFocus?.focus?.();
        }
      };
      openers.forEach((b) => b.addEventListener('click', () => toggle(true)));
      overlay.querySelectorAll('[data-overlay-close]').forEach((b) => b.addEventListener('click', () => toggle(false)));
      overlay.addEventListener('keydown', (e) => e.key === 'Escape' && toggle(false));
    });
  };

  // Recherche prédictive : recharge la section via l'API de recherche Shopify.
  const initSearch = (scope, sectionId) => {
    scope.querySelectorAll('[data-predictive-search]').forEach((box) => {
      if (box.__bw) return;
      box.__bw = true;
      const input = box.querySelector('[data-predictive-search-input]');
      const results = box.querySelector('[data-predictive-search-results]');
      if (!input || !results || !sectionId) return;
      let timer;
      let controller;
      const search = async (q) => {
        controller?.abort();
        controller = new AbortController();
        results.setAttribute('aria-busy', 'true');
        try {
          const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
          const url = `${root}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=6&section_id=${sectionId}`;
          const html = await (await fetch(url, { signal: controller.signal })).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const tpl = doc.querySelector('template[shadowrootmode]');
          const fresh = (tpl ? tpl.content : doc).querySelector('[data-predictive-search-results]');
          if (fresh) results.innerHTML = fresh.innerHTML;
          setOpen(box, true);
        } catch (e) {
          if (e.name !== 'AbortError') console.error('[bw] recherche', e);
        } finally {
          results.removeAttribute('aria-busy');
        }
      };
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        if (q.length < 2) {
          controller?.abort();
          setOpen(box, false);
          return;
        }
        timer = setTimeout(() => search(q), 300);
      });
      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2 && results.innerHTML.trim()) setOpen(box, true);
      });
      box.addEventListener('focusout', (e) => {
        if (!box.contains(e.relatedTarget)) setOpen(box, false);
      });
      box.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          setOpen(box, false);
          input.blur();
        }
      });
    });
  };

  // Les ancres (#fonctionnement) ne traversent pas le shadow DOM : on fait défiler nous-mêmes.
  const findAnchor = (id) => {
    if (!id) return null;
    for (const layout of document.querySelectorAll('bw-layout')) {
      const el = layout.shadowRoot?.getElementById(id);
      if (el) return el;
    }
    return document.getElementById(id);
  };

  const initAnchors = (scope) => {
    if (scope.__bwAnchors) return;
    scope.__bwAnchors = true;
    scope.addEventListener('click', (e) => {
      const link = e.target.closest?.('a[href*="#"]');
      if (!link) return;
      const url = new URL(link.href, location.href);
      if (url.pathname !== location.pathname) return;
      const target = findAnchor(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      e.preventDefault();
      link.closest('[data-overlay]')?.querySelector('[data-overlay-close]')?.click();
      target.scrollIntoView({ behavior: 'smooth' });
    });
  };

  // Arrivée depuis une autre page sur /#fonctionnement.
  const scrollToHash = () => {
    const target = findAnchor(decodeURIComponent(location.hash.slice(1)));
    if (target) target.scrollIntoView();
  };

  const init = (root = document) => {
    upgradeShadowRoots(root);
    root.querySelectorAll('bw-layout').forEach((layout) => {
      const scope = layout.shadowRoot;
      if (!scope) return;
      const sectionId = layout.closest('[data-section-id]')?.getAttribute('data-section-id');
      initSticky(scope);
      initMenus(scope);
      initOverlays(scope);
      initSearch(scope, sectionId);
      initAnchors(scope);
    });
  };

  // Fiche produit (DOM normal, pas de shadow DOM : le bouton de paiement express de Shopify doit y être trouvé).
  const initProduct = (root = document) => {
    root.querySelectorAll('[data-bwp]').forEach((bwp) => {
      if (bwp.__bw) return;
      bwp.__bw = true;

      const track = bwp.querySelector('[data-bwp-track]');
      const thumbs = [...bwp.querySelectorAll('[data-bwp-thumb]')];
      const setCurrent = (i) => thumbs.forEach((t, j) => t.setAttribute('aria-current', String(i === j)));
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const i = Number(thumb.dataset.bwpThumb);
          track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
          setCurrent(i);
        });
      });
      track?.addEventListener('scroll', () => {
        setCurrent(Math.round(track.scrollLeft / track.clientWidth));
      }, { passive: true });

      bwp.querySelectorAll('[data-bwp-qty]').forEach((qty) => {
        const input = qty.querySelector('input');
        qty.querySelectorAll('[data-bwp-step]').forEach((b) => b.addEventListener('click', () => {
          const next = Math.min(Number(input.max) || 99, Math.max(1, (Number(input.value) || 1) + Number(b.dataset.bwpStep)));
          input.value = next;
        }));
      });

      const form = bwp.querySelector('.bwp__form');
      const variant = bwp.querySelector('[data-bwp-variant]');
      const atcMain = bwp.querySelector('[data-bwp-atc]');
      const stickyPrice = bwp.querySelector('[data-bwp-sticky-price]');
      const express = bwp.querySelector('[data-bwp-express]');
      const errorEl = bwp.querySelector('[data-bwp-error]');
      const toyCents = () => Number(variant ? variant.selectedOptions[0].dataset.cents : atcMain?.dataset.soloPrice) || 0;

      // Pack « jouet + écrin » : la remise affichée doit être celle de la réduction automatique du checkout.
      const pack = bwp.querySelector('[data-bwp-pack]');
      // Même rendu que le filtre Liquid `money` (format monétaire de la boutique).
      const money = (cents) => {
        const group = (n, decimals, thousands, decimal) => {
          const [int, frac] = (n / 100).toFixed(decimals).split('.');
          return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
        };
        const formats = {
          amount: () => group(cents, 2, ',', '.'),
          amount_no_decimals: () => group(cents, 0, ',', '.'),
          amount_with_comma_separator: () => group(cents, 2, '.', ','),
          amount_no_decimals_with_comma_separator: () => group(cents, 0, '.', ','),
          amount_with_space_separator: () => group(cents, 2, ' ', ','),
          amount_with_apostrophe_separator: () => group(cents, 2, "'", '.'),
        };
        const format = bwp.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (formats[key] || formats.amount)());
      };
      const isPack = () => pack?.querySelector('[data-bwp-pack-choice]:checked')?.value === 'pack';
      const packVariant = () => pack?.querySelector('input[type="radio"][data-bwp-pack-variant]:checked') || pack?.querySelector('[data-bwp-pack-variant]');
      const setText = (sel, text) => pack.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
      const updatePack = () => {
        const toy = toyCents();
        if (!pack) {
          if (stickyPrice) stickyPrice.textContent = money(toy);
          return;
        }
        const comp = packVariant();
        const full = toy + (Number(comp?.dataset.price) || 0);
        const price = full - (Number(pack.dataset.packDiscount) || 0);
        setText('[data-bwp-solo-price]', money(toy));
        setText('[data-bwp-pack-price]', money(price));
        setText('[data-bwp-pack-was]', money(full));
        pack.querySelector('[data-bwp-pack-ship]')?.toggleAttribute('hidden', price < Number(pack.dataset.threshold));
        const img = pack.querySelector('[data-bwp-pack-img]');
        if (img && comp?.dataset.image) img.src = comp.dataset.image;
        if (stickyPrice) stickyPrice.textContent = money(isPack() ? price : toy);
        // Le paiement express n'achète que le jouet : on le cache quand le pack est choisi.
        if (express) express.hidden = isPack();
      };
      pack?.querySelectorAll('[data-bwp-pack-choice]').forEach((r) => r.addEventListener('change', updatePack));
      pack?.querySelectorAll('[data-bwp-pack-variant]').forEach((r) => r.addEventListener('change', () => {
        const packChoice = pack.querySelector('[data-bwp-pack-choice][value="pack"]');
        if (packChoice) packChoice.checked = true;
        updatePack();
      }));

      variant?.addEventListener('change', () => {
        const opt = variant.selectedOptions[0];
        bwp.querySelectorAll('[data-bwp-price]').forEach((el) => { el.textContent = opt.dataset.price; });
        updatePack();
      });

      const setBusy = (busy) => {
        bwp.querySelectorAll('[data-bwp-atc], [data-bwp-sticky] button').forEach((b) => (busy ? b.setAttribute('aria-busy', 'true') : b.removeAttribute('aria-busy')));
      };
      form?.addEventListener('submit', async (e) => {
        setBusy(true);
        if (!isPack()) return;
        e.preventDefault();
        if (errorEl) errorEl.hidden = true;
        const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
        const items = [
          { id: Number(form.querySelector('[name="id"]').value), quantity },
          { id: Number(packVariant().value), quantity: 1 },
        ];
        const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        try {
          const res = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || '');
          window.location.href = `${root}cart`;
        } catch (err) {
          setBusy(false);
          if (errorEl) {
            errorEl.textContent = err.message || "Oups, l'ajout au panier n'a pas fonctionné. Réessaie dans un instant.";
            errorEl.hidden = false;
          }
        }
      });
      // Retour arrière depuis le panier (cache navigateur) : on réactive les boutons.
      window.addEventListener('pageshow', (e) => e.persisted && setBusy(false));

      // Date de livraison estimée (jours calendaires).
      const eta = bwp.querySelector('[data-bwp-eta]');
      const minDays = Number(eta?.dataset.min);
      const maxDays = Number(eta?.dataset.max);
      if (eta && minDays && maxDays) {
        const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d; };
        const day = (d) => (d.getDate() === 1 ? '1er' : String(d.getDate()));
        const month = (d) => new Intl.DateTimeFormat('fr-FR', { month: 'long' }).format(d);
        const from = inDays(minDays);
        const to = inDays(maxDays);
        const range = from.getMonth() === to.getMonth()
          ? `${day(from)} et le ${day(to)} ${month(to)}`
          : `${day(from)} ${month(from)} et le ${day(to)} ${month(to)}`;
        eta.textContent = `Commande aujourd'hui, reçois-la entre le ${range}`;
        eta.hidden = false;
      }

      // Barre d'achat collante : visible quand le vrai bouton est hors écran.
      const sticky = bwp.querySelector('[data-bwp-sticky]');
      const atc = bwp.querySelector('[data-bwp-atc]');
      if (sticky && atc && 'IntersectionObserver' in window) {
        const stickyBtn = sticky.querySelector('button');
        new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.toggleAttribute('data-visible', show);
          sticky.setAttribute('aria-hidden', String(!show));
          stickyBtn.tabIndex = show ? 0 : -1;
        }).observe(atc);
      }
    });
  };

  // Popup bon de réduction : une seule fois par visiteur (fermée = 7 jours de silence, inscrite = plus jamais).
  const POPUP_KEY = 'bw-popup';
  const readPopup = () => {
    try { return JSON.parse(localStorage.getItem(POPUP_KEY) || 'null'); } catch (e) { return null; }
  };
  const writePopup = (value) => {
    try { localStorage.setItem(POPUP_KEY, JSON.stringify(value)); } catch (e) {}
  };
  const initPopup = (root = document) => {
    const pop = root.querySelector('[data-bwpop]');
    if (!pop || pop.__bw) return;
    pop.__bw = true;
    const state = pop.dataset.state;
    let lastFocus = null;

    const open = () => {
      lastFocus = document.activeElement;
      pop.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      (pop.querySelector('input[type="email"]') || pop.querySelector('.bwpop__btn'))?.focus({ preventScroll: true });
    };
    const close = () => {
      pop.hidden = true;
      document.documentElement.style.overflow = '';
      if (state === 'success') writePopup({ done: true });
      else if (!readPopup()?.done) writePopup({ closedAt: Date.now() });
      lastFocus?.focus?.();
    };

    pop.querySelectorAll('[data-bwpop-close]').forEach((b) => b.addEventListener('click', close));
    pop.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    pop.querySelector('[data-bwpop-copy]')?.addEventListener('click', async (e) => {
      const code = pop.querySelector('[data-bwpop-code]').textContent.trim();
      try { await navigator.clipboard.writeText(code); } catch (err) {}
      e.currentTarget.textContent = 'Copié ✓';
    });

    if (state === 'success') {
      writePopup({ done: true });
      open();
      return;
    }
    if (state === 'error') {
      open();
      return;
    }

    const saved = readPopup();
    const recentlyClosed = saved?.closedAt && Date.now() - saved.closedAt < 7 * 24 * 3600 * 1000;
    if (saved?.done || recentlyClosed || window.Shopify?.designMode) return;
    // Attendre que le bandeau cookies de Shopify soit fermé, sinon il cache la popup.
    const cookieBannerVisible = () => {
      const banner = document.getElementById('shopify-pc__banner');
      return !!banner && banner.getClientRects().length > 0 && getComputedStyle(banner).visibility !== 'hidden';
    };
    const delay = (Number(pop.dataset.delay) || 0) * 1000;
    const schedule = () => setTimeout(() => {
      if (cookieBannerVisible()) waitForBanner();
      else open();
    }, delay);
    const waitForBanner = () => {
      const timer = setInterval(() => {
        if (cookieBannerVisible()) return;
        clearInterval(timer);
        setTimeout(open, 1500);
      }, 500);
    };
    schedule();
  };

  const start = () => {
    init();
    initProduct();
    initPopup();
    if (location.hash) requestAnimationFrame(scrollToHash);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    initProduct(e.target);
  });
})();
